import { useEffect, useMemo, useRef, useState } from "react";
import { getEmployeePerformanceOverview } from "../api/instateamPerformanceApi";
import { AppIcon, StatusBanner } from "../components/InstateamEmployeeComponents";
import { formatCurrencyValue, formatDateValue } from "../utils/employeeHelpers";

const FILTERS = ["Today", "Week", "Month", "Year", "Custom"];
const HEATMAP_ROWS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const HEATMAP_COLS = Array.from({ length: 24 }, (_, index) => index);

function getMonthKey(date = new Date()) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

function getMonthStart(monthKey) {
  return `${monthKey}-01`;
}

function getMonthEnd(monthKey) {
  const [year, month] = String(monthKey || getMonthKey()).split("-").map(Number);
  const date = new Date(year, month, 0);
  return `${year}-${String(month).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function percent(value, total) {
  if (!total) return 0;
  return Math.round((Number(value || 0) / Number(total || 0)) * 100);
}

function formatShortDate(value) {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-IN", { day: "2-digit", month: "short" }).format(date);
}

function formatCsvDate(value) {
  if (!value) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(value))) return `'${value}`;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : `'${date.toISOString().slice(0, 10)}`;
}

function formatMinutes(minutes) {
  const safeMinutes = Math.max(0, Math.round(Number(minutes || 0)));
  const hours = Math.floor(safeMinutes / 60);
  const mins = safeMinutes % 60;
  if (!hours) return `${mins}m`;
  return `${hours}h ${mins}m`;
}

function getComparableDate(value) {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date : null;
}

function getDateRangeLabel(rangeMeta = {}, calendar = [], monthKey = getMonthKey()) {
  if (rangeMeta.startKey && rangeMeta.endKey) {
    return `${formatShortDate(rangeMeta.startKey)} - ${formatShortDate(rangeMeta.endKey)}`;
  }

  const dateKeys = calendar.map((day) => day.dateKey).filter(Boolean).sort();
  if (dateKeys.length) {
    return `${formatShortDate(dateKeys[0])} - ${formatShortDate(dateKeys[dateKeys.length - 1])}`;
  }

  return `${formatShortDate(getMonthStart(monthKey))} - ${formatShortDate(getMonthEnd(monthKey))}`;
}

function getTaskDate(task) {
  return (
    getComparableDate(task.completedAt) ||
    getComparableDate(task.updatedAt) ||
    getComparableDate(task.dueDate) ||
    getComparableDate(task.createdAt)
  );
}

function getTaskTitle(task) {
  return task.title || task.taskTitle || task.subject || task.name || "Assigned task";
}

function getStatusKey(value) {
  return String(value || "").toLowerCase().replace(/\s+/g, "_");
}

function getRangeKey(value) {
  return String(value || "month").toLowerCase();
}

function getCompletedTasks(tasks = []) {
  return tasks.filter((task) =>
    ["completed", "done", "finished"].includes(getStatusKey(task.status))
  );
}

function buildDelta(current, previous, suffix = "%") {
  const difference = Math.round(Number(current || 0) - Number(previous || 0));
  if (!difference) return "No change";
  return `${difference > 0 ? "+" : ""}${difference}${suffix}`;
}

function downloadTextFile(fileName, content) {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8" });
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  window.URL.revokeObjectURL(url);
}

function csvCell(value) {
  return `"${String(value ?? "").replace(/"/g, '""')}"`;
}

function toCsv(rows) {
  return rows.map((row) => row.map(csvCell).join(",")).join("\n");
}

function PerformanceMetricCard({ metric }) {
  return (
    <article className="employee-performance-metric-card">
      <div className={`employee-performance-icon ${metric.tone}`}>
        <AppIcon name={metric.icon} />
      </div>
      <div>
        <span>{metric.label}</span>
        <strong>{metric.value}</strong>
        <small className={metric.deltaTone}>{metric.delta}</small>
      </div>
      <Sparkline values={metric.sparkline} color={metric.color} />
    </article>
  );
}

function Sparkline({ values = [], color = "#7c3aed" }) {
  const points = values.length ? values : [4, 5, 4, 6, 5, 7, 6, 5];
  const max = Math.max(...points, 1);
  const path = points
    .map((value, index) => {
      const x = (index / Math.max(points.length - 1, 1)) * 100;
      const y = 28 - (Number(value || 0) / max) * 22;
      return `${index ? "L" : "M"}${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(" ");

  return (
    <svg className="employee-performance-sparkline" viewBox="0 0 100 32" aria-hidden="true">
      <path d={path} fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  );
}

function TrendChart({ values, labels }) {
  const safeValues = values.length ? values : [0];
  const max = 10;
  const points = safeValues.map((value, index) => {
    const x = 50 + index * (520 / Math.max(safeValues.length - 1, 1));
    const y = 210 - (Number(value || 0) / max) * 170;
    return { x, y, value };
  });
  const linePath = points.map((point, index) => `${index ? "L" : "M"}${point.x} ${point.y}`).join(" ");
  const areaPath = `${linePath} L${points.at(-1)?.x || 570} 220 L50 220 Z`;

  return (
    <svg className="employee-performance-trend" viewBox="0 0 620 240" role="img" aria-label="Performance trend chart">
      {[0, 2, 4, 6, 8, 10].map((line) => (
        <g key={line}>
          <line x1="44" y1={220 - line * 17} x2="590" y2={220 - line * 17} />
          <text x="12" y={224 - line * 17}>{line}</text>
        </g>
      ))}
      <path className="trend-area" d={areaPath} />
      <path className="trend-line" d={linePath} />
      {points.map((point, index) => (
        <g key={`${point.x}-${point.y}`}>
          <circle cx={point.x} cy={point.y} r="5" />
          <text x={point.x - 10} y={point.y - 16}>{Number(point.value || 0).toFixed(1)}</text>
          <text className="trend-label" x={point.x - 20} y="236">{labels[index] || `D${index + 1}`}</text>
        </g>
      ))}
    </svg>
  );
}

function DonutCard({ title, totalLabel, segments }) {
  const safeSegments = segments.filter((segment) => Number(segment.value) > 0);
  let offset = 0;

  return (
    <article className="employee-performance-card">
      <div className="employee-performance-card-head">
        <h3>{title}</h3>
      </div>
      <div className="employee-performance-donut-layout">
        <div className="employee-performance-donut">
          <svg viewBox="0 0 120 120">
            <circle cx="60" cy="60" r="45" className="donut-base" />
            {safeSegments.map((segment) => {
              const dash = Math.max(0, Math.min(100, segment.value));
              const circle = (
                <circle
                  key={segment.label}
                  cx="60"
                  cy="60"
                  r="45"
                  pathLength="100"
                  className="donut-segment"
                  style={{
                    stroke: segment.color,
                    strokeDasharray: `${dash} ${100 - dash}`,
                    strokeDashoffset: -offset,
                  }}
                />
              );
              offset += dash;
              return circle;
            })}
          </svg>
          <div>
            <strong>{totalLabel}</strong>
            <span>Total</span>
          </div>
        </div>
        <div className="employee-performance-legend">
          {segments.map((segment) => (
            <span key={segment.label}>
              <i style={{ background: segment.color }} />
              {segment.label}
              <small>{segment.detail}</small>
            </span>
          ))}
        </div>
      </div>
    </article>
  );
}

function ProductivityGauge({ score }) {
  const rotation = clamp(score, 0, 100) * 1.8 - 90;

  return (
    <article className="employee-performance-card employee-performance-gauge-card">
      <h3>Productivity Score</h3>
      <div className="employee-performance-gauge" style={{ "--gauge-rotation": `${rotation}deg` }}>
        <div className="gauge-arc" />
        <div className="gauge-needle" />
        <strong>{score}%</strong>
        <span>{score >= 80 ? "Great" : score >= 60 ? "Good" : "Needs Focus"}</span>
      </div>
      <p>You are more productive than {score}% of your peers</p>
    </article>
  );
}

function MiniMetric({ icon, tone, label, value, delta, deltaTone = "positive" }) {
  return (
    <article className="employee-performance-mini-card">
      <div className={`employee-performance-icon ${tone}`}>
        <AppIcon name={icon} />
      </div>
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        <small className={deltaTone}>{delta}</small>
      </div>
    </article>
  );
}

function EmptyInline({ children }) {
  return <span className="employee-performance-empty">{children}</span>;
}

export default function EmployeePerformancePage({ token }) {
  const [range, setRange] = useState("Week");
  const [selectedMonth, setSelectedMonth] = useState(getMonthKey);
  const monthPickerRef = useRef(null);
  const [data, setData] = useState({
    rangeMeta: null,
    attendance: null,
    tasks: null,
    payroll: null,
    leaves: null,
    documents: null,
    complaints: null,
    performance: null,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const monthKey = selectedMonth;

  useEffect(() => {
    let isActive = true;

    async function fetchPerformance() {
      if (!token) return;

      try {
        setIsLoading(true);
        setMessage(null);
        const payload = await getEmployeePerformanceOverview(token, {
          range: getRangeKey(range),
          month: monthKey,
        });

        if (!isActive) return;

        const overview = payload.overview || {};
        setData({
          rangeMeta: {
            range: overview.range,
            monthKey: overview.monthKey,
            startKey: overview.startKey,
            endKey: overview.endKey,
          },
          attendance: overview.attendance || null,
          tasks: overview.tasks || null,
          payroll: overview.payroll || null,
          leaves: overview.leaves || null,
          documents: overview.documents || null,
          complaints: overview.complaints || null,
          performance: overview.performance || null,
        });
      } catch (error) {
        if (isActive) setMessage({ type: "error", text: error.message });
      } finally {
        if (isActive) setIsLoading(false);
      }
    }

    fetchPerformance();
    return () => {
      isActive = false;
    };
  }, [monthKey, range, token]);

  const derived = useMemo(() => {
    const rangeMeta = data.rangeMeta || {};
    const attendanceSummary = data.attendance?.summary || {};
    const attendanceCalendar = data.attendance?.calendar || [];
    const attendanceRecords = data.attendance?.records || [];
    const tasks = data.tasks?.tasks || [];
    const taskMetrics = data.tasks?.metrics || {};
    const payrollRecords = data.payroll?.payrollRecords || [];
    const payrollMetrics = data.payroll?.metrics || {};
    const salaryProfile = data.payroll?.salaryProfile || {};
    const leavesMetrics = data.leaves?.metrics || {};
    const leaveBalance = data.leaves?.balance?.summary || [];
    const leaveRequests = data.leaves?.requests || [];
    const documentsMetrics = data.documents?.metrics || {};
    const documents = data.documents?.documents || [];
    const complaints = data.complaints?.complaints || [];
    const backendPerformance = data.performance || {};

    const completedTaskList = getCompletedTasks(tasks);
    const totalTasks = taskMetrics.total || tasks.length;
    const completedTasks =
      taskMetrics.completed ||
      completedTaskList.length;
    const pendingTasks = Math.max(totalTasks - completedTasks, 0);
    const presentDays = attendanceSummary.present || 0;
    const workingDays = attendanceSummary.workingDays || attendanceCalendar.filter((day) => day.status).length || 0;
    const absentDays = attendanceSummary.absent || 0;
    const leavesTaken = leavesMetrics.leavesTaken || leavesMetrics.leavesApproved || 0;
    const grossSalary = payrollMetrics.netSalary || data.payroll?.salaryOverview?.netSalary || 0;
    const verifiedDocs = documentsMetrics.verified || 0;
    const totalDocs = documentsMetrics.total || 0;
    const resolvedComplaints = complaints.filter((complaint) =>
      ["resolved", "closed"].includes(String(complaint.status || "").toLowerCase())
    ).length;

    const attendancePercent = percent(presentDays, workingDays);
    const documentPercent = percent(verifiedDocs, totalDocs || verifiedDocs || 1);
    const complaintPercent = complaints.length ? percent(resolvedComplaints, complaints.length) : 100;
    const score = Number.isFinite(Number(backendPerformance.scorePercent))
      ? clamp(Number(backendPerformance.scorePercent), 0, 100)
      : 0;
    const scoreOutOfTen = Number.isFinite(Number(backendPerformance.score))
      ? clamp(Number(backendPerformance.score), 0, 10)
      : score / 10;

    const recentDays = attendanceCalendar.slice(-8);
    const trendSource = recentDays.length ? recentDays : attendanceRecords.slice(-8);
    const trend = trendSource.map((day) => {
      const status = getStatusKey(day.status);
      const attendancePoints = status === "present" ? 4 : status === "late" ? 3 : status === "half_day" ? 2 : 0;
      const taskPoints = totalTasks ? (completedTasks / Math.max(totalTasks, 1)) * 4 : 0;
      const documentPoints = documentPercent / 50;
      return clamp(attendancePoints + taskPoints + documentPoints, 0, 10);
    });
    const trendLabels = trendSource.map((day, index) =>
      formatShortDate(day.dateKey || day.date || day.createdAt) || `D${index + 1}`
    );
    const dateRangeLabel = getDateRangeLabel(rangeMeta, attendanceCalendar, monthKey);

    const totalWorkedMinutes = attendanceRecords.reduce((sum, record) => {
      const checkIn = getComparableDate(record.checkInAt);
      const checkOut = getComparableDate(record.checkOutAt);
      if (!checkIn || !checkOut || checkOut <= checkIn) return sum;
      return sum + (checkOut.getTime() - checkIn.getTime()) / 60000;
    }, 0);
    const fallbackWorkedMinutes = presentDays * 8 * 60 + (attendanceSummary.halfDay || 0) * 4 * 60;
    const workedMinutes = totalWorkedMinutes || fallbackWorkedMinutes;
    const meetingTaskCount = tasks.filter((task) => /meeting|call|sync/i.test(getTaskTitle(task))).length;
    const learningTaskCount = tasks.filter((task) => /learn|training|course/i.test(getTaskTitle(task))).length;
    const meetingMinutes = Math.min(workedMinutes, meetingTaskCount * 45);
    const learningMinutes = Math.min(workedMinutes - meetingMinutes, learningTaskCount * 60);
    const developmentMinutes = Math.min(
      Math.max(workedMinutes - meetingMinutes - learningMinutes, 0),
      completedTasks * 75
    );
    const deepWorkMinutes = Math.max(workedMinutes - meetingMinutes - learningMinutes - developmentMinutes, 0);
    const timeBuckets = [
      ["Deep Work", deepWorkMinutes, "#3778f6"],
      ["Meetings", meetingMinutes, "#25b95a"],
      ["Development", developmentMinutes, "#f6b300"],
      ["Learning", learningMinutes, "#8b5cf6"],
      ["Others", Math.max(workedMinutes * 0.08, workedMinutes - deepWorkMinutes - meetingMinutes - developmentMinutes - learningMinutes), "#b8beca"],
    ].filter(([, minutes]) => minutes > 0);
    const timeTotal = timeBuckets.reduce((sum, [, minutes]) => sum + minutes, 0);
    const timeSegments = timeBuckets.length
      ? timeBuckets.map(([label, minutes, color]) => ({
          label,
          detail: `${formatMinutes(minutes)} (${percent(minutes, timeTotal)}%)`,
          value: percent(minutes, timeTotal),
          color,
        }))
      : [{ label: "No tracked time", detail: "0m (0%)", value: 100, color: "#b8beca" }];

    const leaveSegments = leaveBalance.length
      ? leaveBalance.map((entry, index) => {
          const used = Number(entry.used || entry.taken || entry.approved || 0);
          const total = Number(entry.total || entry.allowed || entry.balance + used || used || 1);
          return {
            label: entry.label || entry.leaveTypeLabel || entry.key || `Leave ${index + 1}`,
            detail: `${used} / ${total} Days`,
            value: percent(used, total),
            color: ["#3778f6", "#25b95a", "#f6b300", "#8b5cf6", "#ef4444"][index % 5],
          };
        })
      : [
          {
            label: "Leaves Taken",
            detail: `${leavesTaken} Days`,
            value: leavesTaken ? 100 : 0,
            color: "#3778f6",
          },
        ];

    const earningSource = [
      ["Basic Salary", salaryProfile.basicSalary, "#3778f6"],
      ["Allowances", Number(salaryProfile.houseRentAllowance || 0) + Number(salaryProfile.conveyanceAllowance || 0) + Number(salaryProfile.specialAllowance || 0), "#25b95a"],
      ["Deductions", salaryProfile.totalDeductions, "#ef4444"],
      ["Net Salary", grossSalary, "#8b5cf6"],
    ].filter(([, value]) => Number(value) > 0);
    const earningTotal = earningSource.reduce((sum, [, value]) => sum + Number(value || 0), 0);
    const earningSegments = earningSource.length
      ? earningSource.map(([label, value, color]) => ({
          label,
          detail: formatCurrencyValue(value),
          value: percent(value, earningTotal),
          color,
        }))
      : [{ label: "No payroll data", detail: formatCurrencyValue(0), value: 100, color: "#b8beca" }];

    const strengths = [
      completedTasks && completedTasks >= pendingTasks ? "Task Completion" : "",
      attendancePercent >= 80 ? "On-time Attendance" : "",
      documentPercent >= 80 ? "Document Compliance" : "",
      complaintPercent >= 80 ? "Issue Resolution" : "",
      score >= 75 ? "Consistent Performance" : "",
    ].filter(Boolean);
    const improvements = [
      pendingTasks > completedTasks ? "Pending Tasks" : "",
      attendancePercent < 80 ? "Attendance Consistency" : "",
      documentPercent < 80 ? "Document Updates" : "",
      complaintPercent < 80 ? "Complaint Follow-up" : "",
      !workedMinutes ? "Time Tracking" : "",
    ].filter(Boolean);

    const achievements = [
      ...completedTaskList.slice(0, 2).map((task) => ({
        title: `Completed ${getTaskTitle(task)}`,
        date: formatDateValue(getTaskDate(task)),
      })),
      presentDays === workingDays && workingDays
        ? { title: "Maintained full attendance in selected period", date: dateRangeLabel }
        : null,
      verifiedDocs
        ? { title: `${verifiedDocs} document${verifiedDocs === 1 ? "" : "s"} verified`, date: formatDateValue(new Date()) }
        : null,
      resolvedComplaints
        ? { title: `${resolvedComplaints} complaint${resolvedComplaints === 1 ? "" : "s"} resolved`, date: formatDateValue(new Date()) }
        : null,
    ].filter(Boolean).slice(0, 3);

    const goals = tasks.map((task) => {
      const status = getStatusKey(task.status);
      const progress = status === "completed" ? 100 : status === "in_progress" ? 60 : status === "pending" ? 25 : 40;
      return [getTaskTitle(task), progress];
    });

    const heatmap = HEATMAP_ROWS.map((row, rowIndex) =>
      HEATMAP_COLS.map((hour) => {
        const calendarDay = recentDays[rowIndex];
        const status = getStatusKey(calendarDay?.status);
        if (!calendarDay || status === "absent") return 0.12;
        const inWorkWindow = hour >= 9 && hour <= 18;
        const taskBoost = totalTasks ? completedTasks / Math.max(totalTasks, 1) : 0;
        return inWorkWindow ? clamp(0.22 + taskBoost + ((hour + rowIndex) % 4) * 0.12, 0.22, 1) : 0.14;
      })
    );

    return {
      attendanceSummary,
      attendanceCalendar,
      attendanceRecords,
      tasks,
      totalTasks,
      completedTasks,
      pendingTasks,
      presentDays,
      workingDays,
      absentDays,
      leavesTaken,
      grossSalary,
      verifiedDocs,
      totalDocs,
      complaints,
      resolvedComplaints,
      score,
      scoreOutOfTen,
      trend,
      trendLabels,
      dateRangeLabel,
      workedMinutes,
      meetingMinutes,
      meetingTaskCount,
      timeSegments,
      leaveSegments,
      earningSegments,
      strengths,
      improvements,
      achievements,
      goals,
      heatmap,
      leaveRequests,
      documents,
      payrollRecords,
      onTimeTasks: Math.max(completedTasks - Math.min(2, pendingTasks), 0),
    };
  }, [data, monthKey]);

  const metrics = [
    {
      label: "Overall Score",
      value: `${derived.scoreOutOfTen.toFixed(1)} / 10`,
      delta: buildDelta(derived.score, 75),
      deltaTone: "positive",
      icon: "barChart",
      tone: "violet",
      color: "#8b5cf6",
      sparkline: [5, 4, 6, 4, 7, 6, 8, derived.scoreOutOfTen],
    },
    {
      label: "Tasks Completed",
      value: `${derived.completedTasks} / ${derived.totalTasks}`,
      delta: `${percent(derived.completedTasks, derived.totalTasks)}% completion`,
      deltaTone: "positive",
      icon: "calendarCheck",
      tone: "blue",
      color: "#2563eb",
      sparkline: [3, 4, 3, 5, 6, 7, 6, derived.completedTasks],
    },
    {
      label: "On Time Tasks",
      value: `${derived.onTimeTasks} / ${derived.totalTasks}`,
      delta: `${percent(derived.onTimeTasks, derived.totalTasks)}% on time`,
      deltaTone: "positive",
      icon: "checkCircle",
      tone: "green",
      color: "#16a34a",
      sparkline: [2, 3, 4, 4, 5, 6, 5, derived.onTimeTasks],
    },
    {
      label: "Attendance",
      value: `${derived.presentDays} / ${derived.workingDays}`,
      delta: `${derived.attendanceSummary.presentPercentage || percent(derived.presentDays, derived.workingDays)}% Present`,
      deltaTone: "positive",
      icon: "clock",
      tone: "orange",
      color: "#f97316",
      sparkline: [1, 2, 3, 4, 4, 5, 5, derived.presentDays],
    },
    {
      label: "Leaves Taken",
      value: `${derived.leavesTaken} Days`,
      delta: derived.leavesTaken ? `${derived.leavesTaken} day${derived.leavesTaken === 1 ? "" : "s"} used` : "No leaves used",
      deltaTone: derived.leavesTaken ? "negative" : "positive",
      icon: "calendar",
      tone: "red",
      color: "#ef4444",
      sparkline: [2, 1, 1, 2, derived.leavesTaken, 1, 0, 0],
    },
    {
      label: "Total Earning",
      value: formatCurrencyValue(derived.grossSalary || 0),
      delta: derived.grossSalary ? "Latest payroll value" : "Payroll pending",
      deltaTone: "positive",
      icon: "wallet",
      tone: "green",
      color: "#16a34a",
      sparkline: [3, 5, 6, 4, 5, 7, 6, 8],
    },
  ];

  function buildReportRows() {
    return [
      ["Employee Performance Report"],
      ["Range", data.rangeMeta?.range || getRangeKey(range)],
      ["Month", data.rangeMeta?.monthKey || monthKey],
      ["Date Range", derived.dateRangeLabel],
      [],
      ["Summary Metric", "Value"],
      ["Overall Score", `${derived.scoreOutOfTen.toFixed(1)} / 10`],
      ["Overall Score Percent", `${derived.score}%`],
      ["Tasks Completed", `${derived.completedTasks} / ${derived.totalTasks}`],
      ["Task Completion %", `${percent(derived.completedTasks, derived.totalTasks)}%`],
      ["Attendance", `${derived.presentDays} / ${derived.workingDays}`],
      ["Attendance %", `${percent(derived.presentDays, derived.workingDays)}%`],
      ["Absent Days", derived.absentDays],
      ["Leaves Taken", derived.leavesTaken],
      ["Documents Verified", `${derived.verifiedDocs} / ${derived.totalDocs}`],
      ["Complaints Resolved", `${derived.resolvedComplaints} / ${derived.complaints.length}`],
      ["Total Earning", derived.grossSalary],
      ["Worked Time", formatMinutes(derived.workedMinutes)],
      [],
      ["Attendance Records"],
      ["Date", "Status", "Check In", "Check Out", "Work Minutes", "Location"],
      ...derived.attendanceRecords.map((record) => [
        formatCsvDate(record.dateKey || record.date),
        record.status || "",
        record.checkInTime || "",
        record.checkOutTime || "",
        record.workMinutes || 0,
        record.location || "",
      ]),
      [],
      ["Task Records"],
      ["Title", "Status", "Due Date", "Completed At", "Priority"],
      ...derived.tasks.map((task) => [
        getTaskTitle(task),
        task.status || "",
        formatCsvDate(task.dueDate),
        formatCsvDate(task.completedAt),
        task.priority || "",
      ]),
      [],
      ["Leave Records"],
      ["Type", "Status", "From", "To", "Days", "Reason"],
      ...derived.leaveRequests.map((leave) => [
        leave.leaveTypeLabel || leave.leaveType || "",
        leave.status || "",
        formatCsvDate(leave.fromDateKey),
        formatCsvDate(leave.toDateKey),
        leave.totalDays || 0,
        leave.reason || "",
      ]),
      [],
      ["Document Records"],
      ["Document", "Status", "Source", "Created At"],
      ...derived.documents.map((document) => [
        document.documentLabel || document.documentKey || "",
        document.status || "",
        document.source || "",
        formatCsvDate(document.createdAt),
      ]),
      [],
      ["Complaint Records"],
      ["Subject", "Status", "Category", "Created At"],
      ...derived.complaints.map((complaint) => [
        complaint.subject || complaint.title || complaint.reason || "Complaint",
        complaint.status || "",
        complaint.category || "",
        formatCsvDate(complaint.createdAt),
      ]),
      [],
      ["Payroll Records"],
      ["Month", "Status", "Gross Salary", "Deductions", "Net Salary", "CTC Annual"],
      ...derived.payrollRecords.map((record) => [
        record.monthKey || "",
        record.status || "",
        record.earnings?.grossSalary || 0,
        record.deductions?.totalDeductions || 0,
        record.totals?.netSalary || 0,
        record.totals?.ctcAnnual || 0,
      ]),
    ];
  }

  function buildReportText() {
    return buildReportRows()
      .map((row) => (row.length > 2 ? row.join(" | ") : row.join(": ")))
      .join("\n");
  }

  function handleExportReport() {
    downloadTextFile("employee-performance-report.csv", toCsv(buildReportRows()));
    setMessage({ type: "success", text: "Performance report downloaded." });
  }

  async function handleShareReport() {
    const reportText = buildReportText();

    try {
      if (navigator.share) {
        await navigator.share({
          title: "Insta Team Performance Report",
          text: reportText,
        });
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(reportText);
        setMessage({ type: "success", text: "Performance summary copied to clipboard." });
        return;
      } else {
        downloadTextFile("employee-performance-share.txt", reportText);
      }

      setMessage({ type: "success", text: "Performance report shared." });
    } catch (error) {
      setMessage({ type: "warning", text: "Sharing was cancelled or unavailable." });
    }
  }

  function handleFeedbackAction() {
    document.querySelector(".employee-performance-tags-card")?.scrollIntoView({ behavior: "smooth", block: "center" });
    setMessage({
      type: "success",
      text: `Feedback score is ${((derived.score || 0) / 20).toFixed(1)} / 5, calculated from your live performance data.`,
    });
  }

  function handleSetGoalsAction() {
    document.querySelector(".employee-performance-goals")?.scrollIntoView({ behavior: "smooth", block: "center" });
    setMessage({
      type: "success",
      text: derived.goals.length
        ? "Showing your current task goals and progress."
        : "No assigned goals found yet. New task assignments will appear here automatically.",
    });
  }

  function openMonthPicker() {
    const picker = monthPickerRef.current;
    if (!picker) return;

    if (typeof picker.showPicker === "function") {
      picker.showPicker();
    } else {
      picker.focus();
      picker.click();
    }
  }

  return (
    <section className="employee-portal-page employee-performance-page">
      <div className="employee-performance-toolbar">
        <div>
          <h1>Performance Overview</h1>
          <div className="employee-breadcrumb">
            <span>Dashboard</span>
            <span>&gt;</span>
            <span>Performance</span>
          </div>
        </div>
        <div className="employee-performance-controls">
          <button
            type="button"
            className="employee-performance-date"
            title="Select report month"
            onClick={openMonthPicker}
          >
            <AppIcon name="calendar" />
            <span>{derived.dateRangeLabel}</span>
            <input
              ref={monthPickerRef}
              type="month"
              aria-label="Select report month"
              value={selectedMonth}
              onChange={(event) => {
                setSelectedMonth(event.target.value || getMonthKey());
                setRange("Month");
              }}
            />
            <AppIcon name="chevronDown" />
          </button>
          <div className="employee-performance-segments">
            {FILTERS.map((filter) => (
              <button
                key={filter}
                type="button"
                className={range === filter ? "active" : ""}
                onClick={() => setRange(filter)}
              >
                {filter}
              </button>
            ))}
          </div>
          <button type="button" className="employee-performance-export" onClick={handleExportReport}>
            <AppIcon name="download" />
            Export Report
            <AppIcon name="chevronDown" />
          </button>
        </div>
      </div>

      {message ? <StatusBanner type={message.type} text={message.text} onClose={() => setMessage(null)} /> : null}

      <div className="employee-performance-metrics-grid">
        {metrics.map((metric) => (
          <PerformanceMetricCard key={metric.label} metric={metric} />
        ))}
      </div>

      <div className="employee-performance-main-grid">
        <article className="employee-performance-card employee-performance-trend-card">
          <div className="employee-performance-card-head">
            <h3>Performance Trend</h3>
            <select value={range} onChange={(event) => setRange(event.target.value)}>
              <option>Week</option>
              <option>Month</option>
              <option>Year</option>
            </select>
          </div>
          <TrendChart values={derived.trend} labels={derived.trendLabels} />
        </article>

        <DonutCard
          title="Time Distribution"
          totalLabel={formatMinutes(derived.workedMinutes)}
          segments={derived.timeSegments}
        />

        <ProductivityGauge score={derived.score} />
      </div>

      <div className="employee-performance-mini-grid">
        <MiniMetric icon="messageSquare" tone="violet" label="Complaints Raised" value={derived.complaints.length} delta={`${derived.resolvedComplaints} resolved`} />
        <MiniMetric icon="messageSquare" tone="blue" label="Complaint Resolution" value={`${percent(derived.resolvedComplaints, derived.complaints.length)}%`} delta="Based on your tickets" />
        <MiniMetric icon="checkCircle" tone="green" label="Focus Time" value={formatMinutes(Math.max(derived.workedMinutes - derived.meetingMinutes, 0))} delta="From attendance time" />
        <MiniMetric icon="users" tone="orange" label="Meetings Attended" value={derived.meetingTaskCount} delta="Detected from task titles" />
        <MiniMetric icon="fileText" tone="blue" label="Documents Uploaded" value={derived.totalDocs} delta={`${derived.verifiedDocs} verified`} />
        <MiniMetric icon="barChart" tone="red" label="Feedback Score" value={`${(derived.score / 20).toFixed(1)} / 5`} delta="Calculated from performance" />
      </div>

      <div className="employee-performance-lower-grid">
        <article className="employee-performance-card">
          <div className="employee-performance-card-head">
            <h3>Attendance Summary</h3>
          </div>
          <div className="employee-performance-attendance-row">
            {derived.attendanceCalendar.slice(-8).map((day, index) => {
              const status = day.status;
              return (
                <div key={day.dateKey || index}>
                  <span>{day.weekdayLabel || formatShortDate(day.dateKey).split(" ")[1] || "Day"}</span>
                  <small>{day.dayOfMonth || formatShortDate(day.dateKey).split(" ")[0] || index + 1}</small>
                  <i className={status === "absent" ? "absent" : "present"}>
                    <AppIcon name={status === "absent" ? "xCircle" : "checkCircle"} />
                  </i>
                </div>
              );
            })}
            {!derived.attendanceCalendar.length ? <EmptyInline>No attendance data yet</EmptyInline> : null}
          </div>
          <div className="employee-performance-summary-strip">
            <span>Total Days <strong>{derived.workingDays}</strong></span>
            <span>Present Days <strong>{derived.presentDays}</strong></span>
            <span>Half Days <strong>{derived.attendanceSummary.halfDay || 0}</strong></span>
            <span>Absent Days <strong>{derived.absentDays}</strong></span>
            <span>Attendance % <strong>{percent(derived.presentDays, derived.workingDays)}%</strong></span>
          </div>
        </article>

        <DonutCard title="Leave Summary" totalLabel={`${derived.leavesTaken} Days`} segments={derived.leaveSegments} />
        <DonutCard title="Earning Breakdown" totalLabel={formatCurrencyValue(derived.grossSalary || 0)} segments={derived.earningSegments} />

        <article className="employee-performance-card employee-performance-tags-card">
          <h3>Top Strengths</h3>
          <div className="employee-performance-tags success">
            {(derived.strengths.length ? derived.strengths : ["No strengths calculated yet"]).map((tag) => (
              <span key={tag}>{tag}</span>
            ))}
          </div>
          <h3>Areas to Improve</h3>
          <div className="employee-performance-tags warning">
            {(derived.improvements.length ? derived.improvements : ["No improvement areas right now"]).map((tag) => (
              <span key={tag}>{tag}</span>
            ))}
          </div>
        </article>
      </div>

      <div className="employee-performance-bottom-grid">
        <article className="employee-performance-card">
          <div className="employee-performance-card-head">
            <h3>Recent Achievements</h3>
          </div>
          <div className="employee-performance-achievements">
            {(derived.achievements.length ? derived.achievements.slice(0, 3) : [{ title: "No achievements recorded yet", date: derived.dateRangeLabel }]).map((achievement) => (
              <span key={`${achievement.title}-${achievement.date}`}><AppIcon name="checkCircle" /> {achievement.title}<small>{achievement.date}</small></span>
            ))}
          </div>
        </article>

        <article className="employee-performance-card">
          <h3>Weekly Activity Heatmap</h3>
          <div className="employee-performance-heatmap">
            {HEATMAP_ROWS.map((row, rowIndex) => (
              <div key={row}>
                <span>{row}</span>
                {HEATMAP_COLS.map((column) => (
                  <i key={`${row}-${column}`} style={{ opacity: derived.heatmap[rowIndex]?.[column] || 0.12 }} />
                ))}
              </div>
            ))}
          </div>
          <div className="employee-performance-heatmap-scale"><span>Low</span><i /><strong>High</strong></div>
        </article>

        <article className="employee-performance-card">
          <div className="employee-performance-card-head">
            <h3>Goal Progress</h3>
          </div>
          <div className="employee-performance-goals">
            {(derived.goals.length ? derived.goals : [["No assigned goals yet", 0]]).map(([label, value]) => (
              <div key={label}>
                <span>{label}</span>
                <progress max="100" value={value} />
                <strong>{value}%</strong>
              </div>
            ))}
          </div>
        </article>

        <article className="employee-performance-card">
          <h3>Quick Actions</h3>
          <div className="employee-performance-actions">
            <button type="button" onClick={handleExportReport}><AppIcon name="download" /> Download Report</button>
            <button type="button" onClick={handleShareReport}><AppIcon name="share" /> Share Report</button>
            <button type="button" onClick={handleFeedbackAction}><AppIcon name="star" /> Feedback</button>
            <button type="button" onClick={handleSetGoalsAction}><AppIcon name="target" /> Set Goals</button>
          </div>
        </article>
      </div>

      {isLoading ? <div className="employee-performance-loading">Refreshing live performance data...</div> : null}
    </section>
  );
}
